import { CustomSource } from "../models/CustomSource";
import { CustomMouseEvent, mapActive } from "../utils/map-utils";
import { GeoJsonPlannedTrajectoryProps } from "../../cra/models/CustomGeoJsonProperties";
import { CraNode } from "../../cra/models/CraNode";
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import { drawTrajectoryStyles } from "../../pages/map/views/trajectoryMapStyles";
import { MapEventType } from "mapbox-gl";
import { CraWay } from "../../cra/models/CraWay";
import { RootStore } from "../../shared/stores/RootStore";
import { MapMode } from "../../survey/stores/MapStore";
import { LifeCycleStage } from "../../cra/models/LifeCycleStage";

export class PlannedTrajectoryMapSource {
  map: mapboxgl.Map;
  active = false;
  rootStore: RootStore;
  drawControl: MapboxDraw | undefined = undefined;
  drawListenerRemovers: (() => void)[] = [];
  craWaySelectionEventRemovers: (() => void)[] = [];

  constructor(map: mapboxgl.Map, rootStore: RootStore) {
    this.map = map;
    this.rootStore = rootStore;

    if (mapActive(this.map) && !this.map.getSource(sourceName)) {
      this.map.addSource(sourceName, sourceConfig);
      this.map.addLayer(pointLayerConfig);
      this.map.addLayer(directionLayerConfig);
    }
  }

  clear() {
    if (mapActive(this.map)) {
      this.disableWaySelection();
      this.clearDrawControl();

      try {
        this.map.removeLayer(pointLayerConfig.id);
        this.map.removeLayer(directionLayerConfig.id);
        this.map.removeSource(sourceName);
      } catch (e) {
        console.warn("Error while removing layers and sources", e);
      }
    }
  }

  onModeChange(mode: MapMode) {
    if (
      mode === MapMode.HomePage ||
      mode === MapMode.ImportHlaBBox ||
      mode === MapMode.ImportHlaOriginDestination
    ) {
      this.setMapData([]);
      this.active = false;
    } else {
      this.updateMapData();
      this.active = true;
    }

    this.clearDrawControl();
    this.disableWaySelection();

    if (mode === MapMode.SurveyPage) {
      this.enableWaySelection();
    } else if (mode === MapMode.PlanTrajectory) {
      this.configDrawControl();
    }
  }

  onStoreUpdate() {
    if (!this.active) return;

    this.updateMapData();
  }

  private disableWaySelection() {
    this.craWaySelectionEventRemovers.map((fn) => fn());
    this.craWaySelectionEventRemovers = [];
  }

  clearDrawControl() {
    if (mapActive(this.map)) {
      if (this.drawControl) {
        this.drawControl.deleteAll();
        this.map.removeControl(this.drawControl);
        this.drawControl = undefined;
      }
      this.drawListenerRemovers.map((fn) => fn());
      this.drawListenerRemovers = [];
    }
  }

  private updateMapData() {
    const { filterStore, craWayStore } = this.rootStore;

    const selectedCraWay = craWayStore.getSelected();

    const plannedTrajectoryFeatures: GeoJSON.Feature[] = [];

    const shouldShowPlannedTrajectory = (craWay: CraWay) => {
      if (selectedCraWay?.lifeCycleStage === LifeCycleStage.Design) {
        return craWay.plannedTrajectory && craWay.id !== selectedCraWay?.id;
      }
      return craWay.plannedTrajectory && !craWay?.geoJson;
    };

    filterStore.filteredCraWays.forEach((craWay) => {
      if (filterStore.isCraWayVisible(craWay)) {
        if (shouldShowPlannedTrajectory(craWay)) {
          plannedTrajectoryFeatures.push(
            ...craWay.plannedTrajectory!.features.map((feature) => ({
              ...feature,
              properties: {
                craWayId: craWay.id,
              },
            })),
          );
        }
      }
    });

    this.setMapData(plannedTrajectoryFeatures);
  }

  private setMapData(features: GeoJSON.Feature[]) {
    if (mapActive(this.map)) {
      const source = this.map.getSource(sourceName) as mapboxgl.GeoJSONSource;
      source?.setData({
        type: "FeatureCollection",
        features,
      });
    }
  }

  private enableWaySelection() {
    const { craWayStore } = this.rootStore;
    const map = this.map;

    const canvas = map.getCanvasContainer();
    const cursorPointer = () => (canvas.style.cursor = "pointer");
    const cursorDefault = () => (canvas.style.cursor = "");
    map.on("mouseenter", lineLayerName, cursorPointer);
    map.on("mouseleave", lineLayerName, cursorDefault);

    const handleSelectRoute = (e: CustomMouseEvent) => {
      e.preventDefault();
      canvas.style.cursor = "grab";
      const feature = e.features?.[0]!;
      const props = feature.properties as GeoJsonPlannedTrajectoryProps;

      map.once("mouseup", () => {
        canvas.style.cursor = "";
        craWayStore.select(props.craWayId);
      });
    };

    map.on("mousedown", lineLayerName, handleSelectRoute);

    this.craWaySelectionEventRemovers.push(() => {
      map.off("mouseenter", lineLayerName, cursorPointer);
      map.off("mouseleave", lineLayerName, cursorDefault);
      map.off("mousedown", lineLayerName, handleSelectRoute);
    });

    return this;
  }

  configDrawControl() {
    const { craWayStore, routeDesignStore } = this.rootStore;
    const craWay = craWayStore.getSelected()!;

    this.drawControl = new MapboxDraw({
      displayControlsDefault: false,
      styles: drawTrajectoryStyles,
    });

    this.map.addControl(this.drawControl as mapboxgl.IControl);

    routeDesignStore.setNewPlannedTrajectory(craWay.plannedTrajectory);

    if (craWay.plannedTrajectory) {
      this.drawControl.add(craWay.plannedTrajectory);
    }

    const updateArea = () => {
      const geoJson = this.drawControl!.getAll();

      // remove extra properties added by mapbox, like the line id
      const features = geoJson.features.map((feat) => ({
        type: feat.type,
        geometry: feat.geometry,
        properties: {
          craWayId: craWay.id,
        },
      }));

      routeDesignStore.setNewPlannedTrajectory(
        features.length ? { ...geoJson, features } : null,
      );
    };

    const onSelectionChangeHandler = () => {
      const ids = this.drawControl?.getSelectedIds() ?? [];
      routeDesignStore.setSelectedFeatureIds(ids);
    };

    this.map.on("draw.create" as MapEventType, updateArea);
    this.map.on("draw.delete" as MapEventType, updateArea);
    this.map.on("draw.update" as MapEventType, updateArea);
    this.map.on(
      "draw.selectionchange" as MapEventType,
      onSelectionChangeHandler,
    );

    this.drawListenerRemovers.push(() => {
      this.map.off("draw.create", updateArea);
      this.map.off("draw.delete", updateArea);
      this.map.off("draw.update", updateArea);
      this.map.off("draw.selectionchange", onSelectionChangeHandler);
    });
  }

  extendBounds(bounds: mapboxgl.LngLatBounds) {
    const { routeDesignStore, craWayStore, craNodeStore } = this.rootStore;

    const selectedWay = craWayStore.getSelected();
    const selectedNode = craNodeStore.getSelected();

    const source = this.map.getSource(sourceName);

    const geoJson =
      routeDesignStore.newPlannedTrajectory ||
      ((source as any)._data as GeoJSON.FeatureCollection);

    geoJson.features.forEach((feature) => {
      const props = feature.properties as GeoJsonPlannedTrajectoryProps;

      if (shouldIncludeInBounds(props, { selectedNode, selectedWay })) {
        if (feature.geometry.type === "LineString") {
          feature.geometry.coordinates.forEach((position) => {
            bounds.extend([position[0], position[1]]);
          });
        }
      }
    });
  }
}

interface ExtendBoundsOptions {
  selectedWay?: CraWay;
  selectedNode?: CraNode;
}
const shouldIncludeInBounds = (
  props: GeoJsonPlannedTrajectoryProps,
  { selectedWay, selectedNode }: ExtendBoundsOptions,
) => {
  if (selectedNode) return false;
  if (selectedWay) return props.craWayId === selectedWay.id;
  return false;
};

const sourceName = CustomSource.PlannedTrajectory;
const sourceConfig: mapboxgl.GeoJSONSourceSpecification = {
  type: "geojson",
  promoteId: "id",
  data: {
    type: "FeatureCollection",
    features: [],
  },
};

const lineLayerName = `${sourceName}-line`;
const pointLayerConfig: mapboxgl.LineLayerSpecification = {
  id: lineLayerName,
  source: sourceName,
  type: "line",
  layout: {
    "line-cap": "round",
    "line-join": "round",
  },
  paint: {
    "line-color": "#00a498",
    "line-width": 6,
  },
};

const directionLayerName = `${sourceName}-direction`;
const directionLayerConfig: mapboxgl.SymbolLayerSpecification = {
  id: directionLayerName,
  source: sourceName,
  type: "symbol",
  layout: {
    "symbol-placement": "line",
    "symbol-spacing": 30,
    "icon-image": "chevron-icon",
    "icon-rotation-alignment": "map",
    "icon-size": 0.5,
  },
  paint: {
    "icon-color": "#FFFFFF",
  },
};
